import React, { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import * as api from "../../api";

/* -----------------------------------------------------------------------------
 * SocialProofFeed — shows a small toast in the bottom-left corner cycling through
 * REAL recently completed orders. Fetches `/api/social-proof/recent` (public,
 * anonymized) every ~45s so newly completed orders appear automatically as soon
 * as the admin marks them "Completado". Each toast lasts ~6s; cycle every ~20s.
 * --------------------------------------------------------------------------- */
export function SocialProofFeed() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState(null);
  const [visible, setVisible] = useState(false);
  const shownIdsRef = React.useRef(new Set());

  // Poll the public endpoint. Refresh every 45s so any freshly completed order
  // by the admin becomes eligible for display within one cycle.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.getSocialProofRecent(15);
        if (cancelled) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
      } catch (e) {
        console.warn("SocialProofFeed: recent feed fetch failed:", e);
      }
    };
    load();
    const interval = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Rotate through the loaded items every ~20s, showing each for ~6s.
  useEffect(() => {
    if (!items.length) return undefined;
    let cancelled = false;
    let hideTimer = null;

    const buildKey = (it) => `${it.display_name}|${it.package_name}|${it.game_name}|${it.completed_at}`;
    const pickNext = () => {
      // Prefer items we haven't shown yet in this session, else recycle.
      const unseen = items.filter((it) => !shownIdsRef.current.has(buildKey(it)));
      const pool = unseen.length ? unseen : items;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      shownIdsRef.current.add(buildKey(pick));
      // Cap set size so it doesn't grow unbounded
      if (shownIdsRef.current.size > 100) shownIdsRef.current = new Set([buildKey(pick)]);
      return pick;
    };

    const show = () => {
      if (cancelled) return;
      const next = pickNext();
      setMsg(next);
      setVisible(true);
      hideTimer = setTimeout(() => { if (!cancelled) setVisible(false); }, 6000);
    };

    // First toast after 8s so it doesn't collide with page-load toasts.
    const firstDelay = setTimeout(show, 8000);
    const cycle = setInterval(show, 20000);
    return () => {
      cancelled = true;
      clearTimeout(firstDelay);
      clearInterval(cycle);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [items]);

  if (!msg) return null;

  // "Hace X min" relative-time helper for a friendlier feel.
  const relativeTime = (iso) => {
    if (!iso) return "recientemente";
    const t = Date.parse(iso);
    if (isNaN(t)) return "recientemente";
    const diffMin = Math.max(0, Math.floor((Date.now() - t) / 60000));
    if (diffMin < 1) return "hace instantes";
    if (diffMin < 60) return `hace ${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    return `hace ${d} d`;
  };

  return (
    <div
      data-testid="social-proof-feed"
      className={`fixed bottom-4 left-4 z-30 max-w-[300px] transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      aria-live="polite"
    >
      <div className="flex items-start gap-2.5 rounded-2xl bg-[#0d0e14]/95 backdrop-blur-md border border-cyan-500/30 px-3 py-2.5 shadow-[0_10px_30px_-5px_rgba(34,211,238,0.35)]">
        <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-cyan-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-zinc-200 leading-snug">
            <strong className="text-white">{msg.display_name}</strong>
            <span className="text-zinc-500"> acaba de recargar </span>
            <strong className="text-cyan-300">{msg.package_name}</strong>
            <span className="text-zinc-500"> de </span>
            <strong className="text-white">{msg.game_name}</strong>
          </p>
          <p className="text-[9px] font-mono text-zinc-500 mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
            {relativeTime(msg.completed_at)} · pedido verificado
          </p>
        </div>
      </div>
    </div>
  );
}
